import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import { useProfileSections } from '../hooks/useProfileSections';
import { Divider } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import {
  FC,
  RefObject,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { Events } from 'react-scroll';
import NavItem from './NavItem';
import {
  focusNavigationElement,
  getNavContentClass,
  getProfileNavbarClassName,
  updateWindowHistory,
  getNavbarSectionByFocusTarget,
} from './navBarUtils';
import { PROFILE_PAGE_SECTIONS } from '@utils/constants';

export const PROFILE_NAVBAR_BI_TAB_TYPE = 'top tab';
const NAVITEM_HEIGHT_OFFSET_MOBILE = -214;

type Props = {
  isMobileView?: boolean;
  navigationRefs: RefObject<Record<string, HTMLElement>>;
  showBack?: boolean;
  onTabChange?: (tab: string) => void;
  activeTab?: string;
  isMiniContactPanelVisible?: boolean;
};

const NavBar: FC<Props> = ({
  isMobileView,
  showBack,
  onTabChange,
  activeTab,
  navigationRefs,
  isMiniContactPanelVisible,
}) => {
  const NAVITEM_HEIGHT_OFFSET = isMobileView
    ? NAVITEM_HEIGHT_OFFSET_MOBILE
    : -168;
  const FIRST_NAVITEM_HEIGHT_OFFSET = isMobileView
    ? NAVITEM_HEIGHT_OFFSET_MOBILE
    : -160;

  const { t } = useTranslation();
  const { logProfilePageClick } = useProfilePageBi();
  const { isRenderedInModal } = useProfileContext();
  const {
    state: { numberOfResults: numberOfTemplates, visibleTemplatesCount },
  } = useTemplatesContext();

  const {
    shouldShowClientProjects,
    shouldShowServicesAndReviews,
    shouldShowTemplates,
    getValidActiveTab,
    navbarSections,
    haveReviews,
  } = useProfileSections(numberOfTemplates);

  const [activeNavItem, setActiveNavItem] = useState(
    getValidActiveTab(activeTab),
  );
  const [isClickScrolling, setIsClickScrolling] = useState(false);

  const registerScrollEnds = useCallback(
    (section: string) => {
      setIsClickScrolling(true);
      Events.scrollEvent.register('end', function (_to: any, _element: any) {
        setActiveNavItem(section);
        onTabChange?.(section);
        setIsClickScrolling(false);
        updateWindowHistory(section);
        Events.scrollEvent.remove('end');
      });
    },
    [onTabChange],
  );

  const handleSetActiveTab = useCallback(
    (section: string) => {
      if (!isClickScrolling) {
        setActiveNavItem(section);
      }
    },
    [isClickScrolling],
  );

  const handleMobileTabClick = useCallback(
    (section: string, biName?: string, scrollIntoView?: () => void) => {
      registerScrollEnds(section);
      setActiveNavItem(section);
      onTabChange?.(section);
      logProfilePageClick(PROFILE_NAVBAR_BI_TAB_TYPE, biName);
      updateWindowHistory(section);
      scrollIntoView?.();
    },
    [onTabChange, logProfilePageClick],
  );

  const handleDesktopTabClick = useCallback(
    (section: string, biName?: string) => {
      registerScrollEnds(section);
      setActiveNavItem(section);
      onTabChange?.(section);
      logProfilePageClick(PROFILE_NAVBAR_BI_TAB_TYPE, biName);
      focusNavigationElement(navigationRefs, section);
    },
    [registerScrollEnds, onTabChange, logProfilePageClick, navigationRefs],
  );

  const profileNavbarClassName = useMemo(
    () =>
      getProfileNavbarClassName(
        isMobileView,
        isRenderedInModal,
        showBack,
        isMiniContactPanelVisible,
      ),
    [isMobileView, isRenderedInModal, showBack, isMiniContactPanelVisible],
  );

  const navContentClass = useMemo(
    () => getNavContentClass(isMobileView),
    [isMobileView],
  );

  const createActiveTabHandler = useCallback(
    (section: string) => () => handleSetActiveTab(section),
    [handleSetActiveTab],
  );

  // Update navbar active tab when keyboard focus moves into a section
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      if (isClickScrolling) return;
      const hit = getNavbarSectionByFocusTarget(
        navigationRefs,
        navbarSections,
        e.target as Node | null,
      );
      if (hit && hit !== activeNavItem) {
        setActiveNavItem(hit);
        onTabChange?.(hit);
        updateWindowHistory(hit);
      }
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [
    navigationRefs,
    navbarSections,
    isClickScrolling,
    activeNavItem,
    onTabChange,
  ]);

  return (
    <div className={profileNavbarClassName} data-hook="profile-navbar">
      <nav
        aria-label={t('profile-page-navigation-tabs-aria-label')}
        className={navContentClass}
      >
        <ul style={{ margin: isMobileView ? '11px 0' : '12px 0' }}>
          {shouldShowClientProjects && (
            <NavItem
              section={PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS}
              textKey="profile-page-nav-bar-client-projects"
              biName="client projects tab"
              isMobileView={isMobileView}
              isActive={activeNavItem === PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS}
              onMobileClick={handleMobileTabClick}
              onDesktopClick={handleDesktopTabClick}
              offset={FIRST_NAVITEM_HEIGHT_OFFSET}
              onSetActive={createActiveTabHandler(
                PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS,
              )}
              hashSpy={!isClickScrolling}
            />
          )}
          <NavItem
            section={PROFILE_PAGE_SECTIONS.ABOUT}
            textKey="profile-page-nav-bar-about"
            biName="about tab"
            dataHook="navbar-about-tab"
            isMobileView={isMobileView}
            isActive={activeNavItem === PROFILE_PAGE_SECTIONS.ABOUT}
            onMobileClick={handleMobileTabClick}
            onDesktopClick={handleDesktopTabClick}
            offset={NAVITEM_HEIGHT_OFFSET}
            onSetActive={createActiveTabHandler(PROFILE_PAGE_SECTIONS.ABOUT)}
            hashSpy={!isClickScrolling}
          />
          {shouldShowServicesAndReviews && (
            <NavItem
              section={PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS}
              textKey={
                haveReviews
                  ? 'profile-page-nav-bar-services-and-reviews'
                  : 'profile-page-nav-bar-services-and-rates'
              }
              biName="services and reviews tab"
              isMobileView={isMobileView}
              isActive={
                activeNavItem === PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS
              }
              onMobileClick={handleMobileTabClick}
              onDesktopClick={handleDesktopTabClick}
              offset={NAVITEM_HEIGHT_OFFSET}
              onSetActive={createActiveTabHandler(
                PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS,
              )}
              hashSpy={!isClickScrolling}
            />
          )}
          {shouldShowTemplates && (
            <NavItem
              section={PROFILE_PAGE_SECTIONS.TEMPLATES}
              textKey="profile-page-nav-bar-templates"
              biName="templates tab"
              isMobileView={isMobileView}
              isActive={activeNavItem === PROFILE_PAGE_SECTIONS.TEMPLATES}
              count={isMobileView ? numberOfTemplates : undefined}
              onMobileClick={handleMobileTabClick}
              onDesktopClick={handleDesktopTabClick}
              offset={NAVITEM_HEIGHT_OFFSET}
              onSetActive={createActiveTabHandler(
                PROFILE_PAGE_SECTIONS.TEMPLATES,
              )}
              hashSpy={!isClickScrolling}
            />
          )}
        </ul>
      </nav>
      {!isMobileView && <Divider />}
    </div>
  );
};

export default NavBar;
